import { Asset } from './asset';
import { LocationFilter } from './location.filter';
import { IOTObject } from '../object.model';
import { Sensor } from './sensor';
import { OTObject } from './object';

export class Location extends OTObject {
  static emMode = false;
  icon?: 'inactive' | 'critical' | 'warning' | 'working';
  override children: Location[] = [];
  breadcrumbs: Location[] = [];
  filtered = false;
  searchTarget = false;

  originalObjectName!: string; // quick way to implement a dirty flag
  originalNextLayer!: string; // quick way to implement a dirty flag

  get Parent(): Location | null {
    const parent = this.breadcrumbs[this.breadcrumbs.length - 1];
    if (!parent) return null;
    return parent;
  }

  get IsAsset(): boolean {
    return this.curLayer === 'h';
  }

  get IsAssociatedEquipment(): boolean {
    return this.curLayer === 'g';
  }

  get NextLayerIsAssociatedEquipment(): boolean {
    return this.nextLayer === 'g';
  }

  get NextLayerIsEquipment(): boolean {
    return this.nextLayer === 'h';
  }

  get IsBuilding(): boolean {
    return this.layerID === 4;
  }

  get IsArea(): boolean {
    return this.layerID === 10;
  }

  get IsAboveEquipment(): boolean {
    return this.nextLayer === 'h' || this.children.filter((item) => item.isAsset).length > 0;
  }

  get NextLayerIsAboveEquipment(): boolean {
    return !!this.children && this.children.filter((item) => item.IsAboveEquipment).length > 0;
  }

  get AssociatedEquipments(): Location[] {
    if (Location.emMode) {
      if (this.NextLayerIsAboveEquipment) return [...this.children];
      if (this.IsAboveEquipment) return [this];
      return this.children.map((item: Location) => item.AssociatedEquipments).flat(2);
    }
    if (this.NextLayerIsAssociatedEquipment) return [...this.children];
    if (this.IsAssociatedEquipment) return [this];
    return this.children.map((item: Location) => item.AssociatedEquipments).flat(2);
  }

  get Assets(): Asset[] {
    if (Location.emMode) {
      // EM: only include equipment with sensors or archived+lifetime-connected
      if (
        this.IsEquipment &&
        (this.numSensors! > 0 || (this.isArchived && this.isLifetimeConnected))
      )
        return [new Asset(this, this.breadcrumbs)];
      return this.children.map((item: Location) => item.Assets).flat(2);
    }
    if (this.IsAsset) return [new Asset(this, this.breadcrumbs)];
    return this.children.map((item: Location) => item.Assets).flat(2);
  }

  get NumSensors(): number {
    if (Location.emMode) {
      return this.Assets.map((asset: any) => asset.numSensors || 0).reduce(
        (a: number, b: number) => a + b,
        0
      );
    }
    return 0;
  }

  get Sensors(): Sensor[] {
    if (this.IsAsset) return this.sensors || [];
    return this.children.map((item: Location) => item.Sensors).flat(2);
  }

  get Critical(): Asset[] {
    if (this.IsAsset)
      return this.critical && !this.inactive && !this.isArchived
        ? [new Asset(this, this.breadcrumbs)]
        : [];
    return this.children.map((item) => item.Critical).flat(2);
  }

  get Warning(): Asset[] {
    if (this.IsAsset)
      return !this.critical && this.warning && !this.inactive && !this.isArchived
        ? [new Asset(this, this.breadcrumbs)]
        : [];
    return this.children.map((item) => item.Warning).flat(2);
  }

  get Working(): Asset[] {
    if (this.IsAsset)
      return !this.critical &&
        !this.warning &&
        !this.inactive &&
        !this.isArchived &&
        (!Location.emMode || (this.isLifetimeConnected !== false && this.NumSensors > 0))
        ? [new Asset(this, this.breadcrumbs)]
        : [];
    return this.children.map((item) => item.Working).flat(2);
  }

  get InActive(): Asset[] {
    if (this.IsAsset)
      return this.inactive && !this.isArchived ? [new Asset(this, this.breadcrumbs)] : [];
    return this.children.map((item) => item.InActive).flat(2);
  }

  get Dirty(): boolean {
    return (
      this.objectID < 0 ||
      this.originalObjectName !== this.objectName ||
      this.originalNextLayer !== this.nextLayer ||
      this.delete ||
      false
    );
  }

  get CustomerName(): string | null {
    if (this.customerName) {
      return this.customerName;
    }

    const childWithCustomer = this.children.flat(2).find((c) => !!c.CustomerName);
    return childWithCustomer ? childWithCustomer.CustomerName : null;
  }

  get MACs(): string | null {
    return this.macs
      ? this.macs
          .split(',')
          .map((source: string) => source['replaceAll'](':', ''))
          .filter((source: string) => source.length === 12)
          .reduce((a: string, b: string) => a + ',' + b, '')
      : null;
  }

  get GatewayIDs(): string | null {
    return this.gatewayIDs
      ? this.gatewayIDs
          .split(',')
          .map((source: string) => source['replaceAll'](':', ''))
          .reduce((a: string, b: string) => a + ',' + b, '')
      : null;
  }

  get IsEquipment(): boolean {
    return this.layerID === 8; // layerID = 8 is equipment
  }

  get Archived(): Asset[] {
    if (this.IsEquipment)
      return this.isArchived && this.isLifetimeConnected ? [new Asset(this, this.breadcrumbs)] : [];
    return this.children.map((item) => item.Archived).flat(2);
  }

  get AssetsWithoutSensors(): Asset[] {
    return this.Assets.filter((asset: any) => !asset.isArchived && asset.numSensors === 0);
  }
  constructor(
    obj: IOTObject,
    nav: IOTObject[] | null,
    breadcrumbs: Location[] = [],
    filtered = false,
    searchTarget = false
  ) {
    super();
    Object.assign(this, obj);

    this.originalObjectName = obj.objectName;
    this.originalNextLayer = obj.nextLayer;

    this.breadcrumbs = breadcrumbs;
    this.filtered = filtered;
    this.searchTarget = searchTarget;

    if (nav) {
      const collator = new Intl.Collator(undefined, {
        numeric: true,
        sensitivity: 'base',
      });
      const sortFn = (a: IOTObject, b: IOTObject) => {
        // sort display order DESC and object Name ASC
        if (a.displayOrder !== b.displayOrder) return (b.displayOrder || 0) - (a.displayOrder || 0);
        return collator.compare(a.objectName, b.objectName);
      };

      this.children = nav
        .filter((item) => item?.parentID === this.objectID)
        .sort(sortFn)
        .map((item: IOTObject) => new Location(item, nav, [...this.breadcrumbs, this]))
        .sort(sortFn) as Location[];

      if (Location.emMode && !this.geoPosition) {
        const temp = this.children.find((item) => !!item.geoPosition);
        if (temp) this.geoPosition = temp.geoPosition;
      }
    }
  }

  public findLocation(id: number): any {
    if (this.objectID === id) return this;
    return this.children
      .map((building: Location) => building.findLocation(id))
      .find((item: Location) => !!item && item.objectID === id);
  }
  public ApplySearch(searchTerm: string): Location | null {
    if (
      !searchTerm ||
      this.objectName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (!!this.edgeDeviceIDs &&
        this.edgeDeviceIDs.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (!!this.sensorDigitIDs &&
        this.sensorDigitIDs.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (!!this.MACs &&
        this.MACs.toLowerCase().includes(searchTerm['replaceAll'](':', '').toLowerCase())) ||
      (!!this.GatewayIDs &&
        this.GatewayIDs.toLowerCase().includes(searchTerm['replaceAll'](':', '').toLowerCase())) ||
      (Location.emMode && !!this.objectID && this.objectID.toString().includes(searchTerm))
    ) {
      this.searchTarget = !!searchTerm;
      return Object.assign(new Location(this, null, this.breadcrumbs, false, !!searchTerm), {
        ...this,
      });
    } else {
      this.searchTarget = false;
    }

    if (!Location.emMode && this.sensors) {
      const _sensors = this.sensors.filter((item: Sensor) =>
        item.sensorDisplayLabel.toLowerCase().includes(searchTerm)
      );
      if (_sensors.length > 0) {
        return this;
      }
    }

    if (Location.emMode ? this.isAsset : this.IsAsset) return null;
    // if (!this.children || this.children.length === 0) return null;

    const _children = this.children
      .map((item: Location) => item.ApplySearch(searchTerm))
      .filter((item) => !!item);

    if (_children.length > 0) {
      return Object.assign(new Location(this, null, this.breadcrumbs), {
        ...this,
        children: _children,
        filtered: true,
      });
    } else {
      return null;
    }
  }

  public ApplyFilter(filter: LocationFilter): Location | null {
    if (!!filter.location && !!filter.location.length && filter.location[0] !== this.objectID) {
      return null;
    }

    if (!Location.emMode) {
      if (!!filter.customer && this.curLayer === 'c' && filter.customer !== this.CustomerName) {
        return null;
      }
    }

    const childFilter = Location.emMode
      ? Object.assign(new LocationFilter(filter), {
          location: [...filter.location].splice(1),
        })
      : new LocationFilter({
          ...filter,
          location: [...filter.location].slice(1),
        });

    let _children: Location[];
    if (Location.emMode) {
      _children = this.children
        .filter(
          (item: Location) =>
            !item.isAsset ||
            ((filter.selectedTypes.length === 0 ||
              filter.selectedTypes.includes(item.equipmentType.toLocaleLowerCase())) &&
              (!filter.warning || (item.warning && !item.inactive && !item.critical)) &&
              (!filter.inactive || item.inactive) &&
              (!filter.critical || (item.critical && !item.inactive)) &&
              (!filter.working || (!item.inactive && !item.critical && !item.warning)) &&
              (!filter.customer || item.customerName === filter.customer))
        )
        .map((item: Location) => item.ApplyFilter(childFilter))
        .filter((item) => !!item)
        .filter((item) => item!.Assets.length > 0);
    } else {
      _children = this.children
        .filter(
          (item: Location) =>
            !item.IsAsset ||
            ((filter.selectedTypes.length === 0 ||
              filter.selectedTypes.includes(item.equipmentType.toLocaleLowerCase())) &&
              (!filter.warning || item.warning) &&
              (!filter.critical || item.critical) &&
              (!filter.working || (!item.critical && !item.warning)) &&
              (!filter.deInstalled || (!item.critical && !item.warning && !item.Working)) &&
              (!filter.customer || item.CustomerName === filter.customer))
        )
        .map((item: Location) => item.ApplyFilter(childFilter))
        .filter((item) => !!item);
    }

    const filtered = !filter.IsOnlyFilteringCustomer && (filter.IsFiltering || this.filtered);

    if (_children.length > 0 || (Location.emMode ? this.isAsset : this.IsAsset || this.isAsset)) {
      return Object.assign(new Location(this, [], []), {
        ...this,
        children: _children,
        filtered: filtered,
      });
    } else {
      return null;
    }
  }

  public ApplyNotificationFilter(notifications: Notification[]): any {
    return this;
    if (this.isAsset && !notifications.find((item: any) => item.objectID === this.objectID)) {
      return null;
    }

    const _children = this.children
      .map((item: Location) => item.ApplyNotificationFilter(notifications))
      .filter((item) => !!item)
      .filter((item) => item.Assets.length > 0);

    if (_children.length > 0 || this.isAsset) {
      return Object.assign(new Location(this, [], []), {
        ...this,
        children: _children,
      });
    } else {
      return null;
    }
  }

  public find(id: number): OTObject | undefined {
    if (this.objectID === id) return this;
    return this.children
      .map((building: Location) => building.find(id))
      .find((item: OTObject | undefined) => !!item && item.objectID === id);
  }

  public getNextLayer(parentBuildingArea: Location): string {
    return this.getFollowingLayer(
      parentBuildingArea,
      this.curLayer,
      this.objectID === parentBuildingArea.objectID
    );
  }

  public getNextNextLayer(parentBuildingArea: Location): string {
    const nextLayer = this.getFollowingLayer(
      parentBuildingArea,
      this.curLayer,
      this.objectID === parentBuildingArea.objectID
    );

    return this.getFollowingLayer(parentBuildingArea, nextLayer, false);
  }

  private getFollowingLayer(
    parentBuildingArea: Location,
    startLayer: string,
    isRoot = false
  ): string {
    const rootCurLayer = parentBuildingArea.curLayer;

    // Decide hierarchy from ROOT type first, not from current child state
    const isAreaHierarchy =
      parentBuildingArea.IsArea || rootCurLayer === 'k' || ['k', 'l', 'm'].includes(startLayer);

    const isBuildingHierarchy =
      parentBuildingArea.IsBuilding || rootCurLayer === 'd' || ['d', 'e', 'f'].includes(startLayer);

    // AREA FLOW: k -> l -> m -> g -> h
    if (isAreaHierarchy && !isBuildingHierarchy) {
      // Root area itself
      if (isRoot) {
        if (parentBuildingArea.structureFloorZone) return 'l'; // area -> zone
        if (parentBuildingArea.structureSectionCrossing) return 'm'; // area -> crossing
        if (parentBuildingArea.structureAssociatedEquipment) return 'g'; // area -> assoc equipment
        return 'h';
      }

      if (parentBuildingArea.structureSectionCrossing && startLayer === 'l') {
        return 'm'; // zone -> crossing
      }

      if (
        parentBuildingArea.structureAssociatedEquipment &&
        (startLayer === 'l' || startLayer === 'm')
      ) {
        return 'g'; // zone/crossing -> associated equipment
      }

      return 'h';
    }

    // BUILDING FLOW: d -> e -> f -> g -> h
    if (isBuildingHierarchy) {
      // Root building itself
      if (isRoot) {
        if (parentBuildingArea.structureFloorZone) return 'e'; // building -> floor
        if (parentBuildingArea.structureSectionCrossing) return 'f'; // building -> section
        if (parentBuildingArea.structureAssociatedEquipment) return 'g'; // building -> assoc equipment
        return 'h';
      }

      if (parentBuildingArea.structureSectionCrossing && startLayer === 'e') {
        return 'f'; // floor -> section
      }

      if (
        parentBuildingArea.structureAssociatedEquipment &&
        (startLayer === 'e' || startLayer === 'f')
      ) {
        return 'g'; // floor/section -> associated equipment
      }

      return 'h';
    }

    // Safe fallback
    return 'h';
  }
}
export class newBuilding {
  buildingName = '';
  areaName = '';
  displayValidation = false;
  greyFlag = false;
  showBorder = false;
  showAlert = false;
  buttonDisabled = false;
}
